import {
	ENVIRONMENT_LABEL_PREFIX,
	LABEL_DELIMITER,
	UNIT_ID_LABEL_PREFIX,
	WARNING_EMOJI
} from 'src/common/consts/platformConfigConsts';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { ILabelConfig } from '../types/LabelsTypes';

const SPECIAL_LABEL_VALUES_WITHOUT_WARNING = new Set([
	'no option',
	'all options',
	'__ev_no_option__'
]);

/**
 * Checks if a value exists in a ConfigurationLabel's values array (case-insensitive)
 */
const isKnownValue = (value: string, labelConfig?: ConfigurationLabel): boolean => {
	if (!labelConfig?.values) {
		return false;
	}

	const valueLower = value.toLowerCase();
	return labelConfig.values.some((configValue) => {
		const configValueStr = typeof configValue === 'string' ? configValue : configValue.value;
		return configValueStr.toLowerCase() === valueLower;
	});
};

type LabelDisplayConfig = {
	displayName: string;
	labelConfig?: ConfigurationLabel;
	extractValue: (label: string) => string;
	isLabelString: (label: string) => boolean;
};

export type DisplayLabel = {
	label: string;
	tooltipContent?: string;
};

const isUnknownValue = (value: string, labelConfig?: ConfigurationLabel): boolean => {
	const hasConfiguredValues = !!labelConfig?.values?.length;
	const shouldSkipWarning = SPECIAL_LABEL_VALUES_WITHOUT_WARNING.has(value.trim().toLowerCase());

	return !isKnownValue(value, labelConfig) && hasConfiguredValues && !shouldSkipWarning;
};

const formatLabel = (value: string, displayName: string, isUnknown: boolean): string =>
	`${isUnknown ? `${WARNING_EMOJI} ` : ''}${displayName}: ${value}`;

export const buildUnknownValueTooltip = (displayName: string, value: string): string =>
	`${displayName}${LABEL_DELIMITER} ${value}\n${WARNING_EMOJI} Unknown value`;

/**
 * Formats a label value with optional warning emoji if the value is not in the configured values
 */
export const formatLabelDisplayValue = (
	value: string,
	displayName: string,
	labelConfig?: ConfigurationLabel
): string => {
	if (!value || !displayName) {
		return value || '';
	}

	return formatLabel(value, displayName, isUnknownValue(value, labelConfig));
};

/**
 * Extracts the value part from a label string given a prefix
 * Handles case-insensitive matching and extracts everything after the colon
 */
export const extractLabelValue = (label: string, prefix: string): string => {
	if (!label) {
		return '';
	}

	const lowerLabel = label.toLowerCase();
	const lowerPrefix = prefix.toLowerCase();

	if (lowerLabel.startsWith(lowerPrefix)) {
		// Find the colon position in the original label (case-insensitive)
		const colonIndex = label.toLowerCase().indexOf(':');
		if (colonIndex >= 0) {
			return label.substring(colonIndex + 1).trim();
		}
		return label.substring(prefix.length).trim();
	}

	return label.trim();
};

/**
 * Checks if a label string starts with a given prefix (case-insensitive)
 */
export const isLabelString = (label: string, prefix: string): boolean => {
	if (!label) {
		return false;
	}
	return label.toLowerCase().startsWith(prefix.toLowerCase());
};

/**
 * Transforms labels array for display, adding warning emojis for unknown values
 * Supports both unitId and environment labels
 */
export const transformLabelsForDisplay = (
	labels: string[],
	isNewPlatformEnabled: boolean,
	unitIdConfig?: LabelDisplayConfig,
	environmentConfig?: LabelDisplayConfig
): string[] =>
	transformLabelsForChips(labels, isNewPlatformEnabled, unitIdConfig, environmentConfig).map(
		({ label }) => label
	);

export const transformLabelsForChips = (
	labels: string[],
	isNewPlatformEnabled: boolean,
	unitIdConfig?: LabelDisplayConfig,
	environmentConfig?: LabelDisplayConfig
): DisplayLabel[] => {
	if (!isNewPlatformEnabled || (!unitIdConfig?.displayName && !environmentConfig?.displayName)) {
		return labels.map((label) => ({ label }));
	}

	return labels.map((label) => {
		const config = [unitIdConfig, environmentConfig].find(
			(labelConfig) => labelConfig?.isLabelString(label) && labelConfig.displayName
		);

		if (!config) {
			return { label };
		}

		const value = config.extractValue(label);
		if (!value) {
			return { label: value };
		}

		const isUnknown = isUnknownValue(value, config.labelConfig);

		const displayLabel: DisplayLabel = {
			label: formatLabel(value, config.displayName, isUnknown)
		};

		if (isUnknown) {
			displayLabel.tooltipContent = buildUnknownValueTooltip(config.displayName, value);
		}

		return displayLabel;
	});
};

export const transformCoreLabelsToCustomLabels = (
	coreLabels: ConfigurationLabel[]
): ILabelConfig[] => {
	return (coreLabels ?? []).map((label) => ({
		key: label.label,
		name: label.displayName ?? label.label,
		description: '',
		options: label.values.map((value) => {
			const valueStr = typeof value === 'string' ? value : value.value;
			return { key: valueStr, name: valueStr };
		})
	}));
};

export const applyMobileLabelDisplayNames = (
	labels: string[],
	unitIdDisplayName?: string,
	environmentDisplayName?: string
): string[] =>
	transformLabelsForDisplay(
		labels,
		true,
		unitIdDisplayName
			? {
					displayName: unitIdDisplayName,
					extractValue: (label: string) => extractLabelValue(label, UNIT_ID_LABEL_PREFIX),
					isLabelString: (label: string) => isLabelString(label, UNIT_ID_LABEL_PREFIX)
				}
			: undefined,
		environmentDisplayName
			? {
					displayName: environmentDisplayName,
					extractValue: (label: string) => extractLabelValue(label, ENVIRONMENT_LABEL_PREFIX),
					isLabelString: (label: string) => isLabelString(label, ENVIRONMENT_LABEL_PREFIX)
				}
			: undefined
	);
